import { Label } from "@shared/ui/Label";
import { useRef, useEffect, useState } from "react";
import { useLogger } from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../../../providers/DevicesProvider";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";

const MicrophoneActivityPreview = () => {
  const logger = useLogger();

  const { supportsAudio } = useBrowserCompatibilityFeatures();

  const { selectedAudioInputDevice } = useDevices();

  const [audioLevel, setAudioLevel] = useState(0);

  const audioContextRef = useRef<AudioContext | null>(null);

  const analyserRef = useRef<AnalyserNode | null>(null);

  const streamRef = useRef<MediaStream | null>(null);

  const animationFrameRef = useRef<number>();

  const mountedRef = useRef(true);

  // Cleanup function
  const cleanup = async () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  useEffect(() => {
    const setupAudioAnalysis = async () => {
      try {
        // Clean up previous setup
        await cleanup();

        if (!selectedAudioInputDevice || !mountedRef.current) {
          return;
        }

        // Create new audio context and analyzer
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;

        // Get audio stream from selected device
        streamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: selectedAudioInputDevice },
          },
        });

        // Connect stream to analyzer
        const source = audioContextRef.current.createMediaStreamSource(
          streamRef.current
        );
        source.connect(analyserRef.current);

        // Start analysis loop
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

        const updateAudioLevel = () => {
          if (!mountedRef.current || !analyserRef.current) return;

          analyserRef.current.getByteFrequencyData(dataArray);

          // Calculate average volume level
          const average =
            dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
          const normalizedLevel = Math.min(1, average / 128); // Normalize to 0-1 range

          setAudioLevel(normalizedLevel);
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };

        updateAudioLevel();
      } catch (error) {
        logger.error(`Error setting up audio analysis: ${error}`);
        // Reset state on error
        setAudioLevel(0);
      }
    };

    setupAudioAnalysis();
  }, [selectedAudioInputDevice, logger]);

  // Calculate the width of the filled portion of the bar
  const barWidth = `${Math.max(0, Math.min(100, audioLevel * 100))}%`;

  if (!supportsAudio) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <Label>Microphone activity</Label>
      <div className="w-full h-2 bg-brand-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-500 transition-all duration-100 ease-out"
          style={{ width: barWidth }}
        />
      </div>
    </div>
  );
};

export default MicrophoneActivityPreview;
